import { createSharedComposable } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { nextTick } from 'vue'
import type { PortComplex, TerminalName } from '~/services/apiClient/api'
import { TERMINAL_PORT_COMPLEX_MAP } from '~/constants'
import { useUserStore } from '~/stores/user'

// Allow tests/stories to override behavior without needing module mocking
let _overrideGlobalFiltersForTesting:
  | null
  | (() => {
      portComplexes: Ref<PortComplex[]>
      setPortComplexes: (value: PortComplex[]) => Promise<void>
      clearPortComplexes: () => void
      terminalMatchesPortComplexes: (terminal: TerminalName) => boolean
    }) = null

export function __setUseGlobalFiltersOverride(
  impl: typeof _overrideGlobalFiltersForTesting
): void {
  _overrideGlobalFiltersForTesting = impl
}

function _useGlobalFilters() {
  if (_overrideGlobalFiltersForTesting) {
    return _overrideGlobalFiltersForTesting()
  }
  const userStore = useUserStore()
  const router = useRouter()
  // Array (CSV) synchronized to the URL via transform. Seed from local storage on first load.
  const portComplexes = useRouteQuery<null | string | string[], PortComplex[]>(
    'port_complexes',
    // Default value is from local storage
    userStore.portComplexesLocalStorage
      ? (userStore.portComplexesLocalStorage
          .split(',')
          .filter(Boolean) as PortComplex[])
      : [],
    {
      mode: 'push',
      transform: (value) => {
        if (!value) return []
        if (Array.isArray(value)) return value as PortComplex[]
        return (value as string).split(',').filter(Boolean) as PortComplex[]
      },
    }
  )

  // Keep local storage in sync in ONE place
  watch(
    portComplexes,
    (arr) => {
      if (arr.length === 0) {
        userStore.portComplexesLocalStorage = undefined
      } else {
        userStore.portComplexesLocalStorage = arr.join(',')
      }
    },
    { immediate: true }
  )

  async function setPortComplexes(value: PortComplex[]) {
    portComplexes.value = value
    await nextTick()
  }
  router.beforeEach((to) => {
    // inject port_complexes into the query if filters are active but not yet in the URL
    if (!to.query.port_complexes && portComplexes.value.length > 0) {
      return {
        path: to.path,
        query: { ...to.query, port_complexes: [...portComplexes.value] },
        hash: to.hash,
      }
    }
  })
  function clearPortComplexes() {
    portComplexes.value = []
    // updating local storage here to avoid a race condition
    userStore.portComplexesLocalStorage = undefined
  }
  function terminalMatchesPortComplexes(terminal: TerminalName): boolean {
    if (!userStore.activatedTerminals.has(terminal)) return false
    if (portComplexes.value.length === 0) return true
    const terminalPortComplex = TERMINAL_PORT_COMPLEX_MAP.get(terminal)
    if (!terminalPortComplex) return false // Bit of a judgment call whether to include
    return portComplexes.value.includes(terminalPortComplex)
  }

  return {
    portComplexes,
    setPortComplexes,
    clearPortComplexes,
    terminalMatchesPortComplexes,
  }
}

export const useGlobalFilters = createSharedComposable(_useGlobalFilters)
